import React, { useState, useEffect, useRef, useCallback } from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import { supabase } from 'lib/supabase';
import { useAuth } from 'contexts/AuthContext';

function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDate(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const diff = now - d;
  if (diff < 86400000) return 'Today';
  if (diff < 172800000) return 'Yesterday';
  return d?.toLocaleDateString();
}

export default function DirectMessagingPanel({ isOpen, onClose, initialMember }) {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const channelRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data, error } = await supabase?.from('conversations')?.select(`
          *,
          p1:user_profiles!conversations_participant_1_fkey(id, full_name, avatar_url, experience_level),
          p2:user_profiles!conversations_participant_2_fkey(id, full_name, avatar_url, experience_level)
        `)?.or(`participant_1.eq.${user?.id},participant_2.eq.${user?.id}`)?.order('last_message_at', { ascending: false });

      if (!error && data) {
        const convs = await Promise.all(data?.map(async (conv) => {
          const other = conv?.participant_1 === user?.id ? conv?.p2 : conv?.p1;
          const { count } = await supabase?.from('direct_messages')?.select('*', { count: 'exact', head: true })?.eq('conversation_id', conv?.id)?.eq('is_read', false)?.neq('sender_id', user?.id);
          const { data: lastMsg } = await supabase?.from('direct_messages')?.select('content, created_at')?.eq('conversation_id', conv?.id)?.order('created_at', { ascending: false })?.limit(1)?.maybeSingle();
          return { ...conv, otherMember: other, unreadCount: count || 0, lastMessage: lastMsg };
        }));
        setConversations(convs);
      }
    } catch (err) {
      console.error('Load conversations error:', err);
    }
  }, [user?.id]);

  const loadMessages = useCallback(async (conversationId) => {
    if (!conversationId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('direct_messages')?.select(`*, sender:user_profiles!direct_messages_sender_id_fkey(id, full_name, avatar_url)`)?.eq('conversation_id', conversationId)?.order('created_at', { ascending: true });
      if (!error && data) {
        setMessages(data);
        // Mark as read
        await supabase?.from('direct_messages')?.update({ is_read: true })?.eq('conversation_id', conversationId)?.neq('sender_id', user?.id);
      }
    } catch (err) {
      console.error('Load messages error:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const startConversationWithMember = useCallback(async (member) => {
    if (!user?.id || !member?.id) return;
    try {
      // Check existing conversation
      const { data: existing } = await supabase?.from('conversations')?.select('*')?.or(
          `and(participant_1.eq.${user?.id},participant_2.eq.${member?.id}),and(participant_1.eq.${member?.id},participant_2.eq.${user?.id})`
        )?.maybeSingle();

      let conv = existing;
      if (!conv) {
        const { data: newConv, error } = await supabase?.from('conversations')?.insert({ participant_1: user?.id, participant_2: member?.id })?.select()?.single();
        if (!error) conv = newConv;
      }
      if (conv) {
        setActiveConversation({ ...conv, otherMember: member });
        await loadMessages(conv?.id);
        await loadConversations();
      }
    } catch (err) {
      console.error('Start conversation error:', err);
    }
  }, [user?.id, loadMessages, loadConversations]);

  useEffect(() => {
    if (isOpen && user?.id) {
      loadConversations();
    }
  }, [isOpen, user?.id, loadConversations]);

  useEffect(() => {
    if (isOpen && initialMember && user?.id) {
      startConversationWithMember(initialMember);
    }
  }, [isOpen, initialMember, user?.id, startConversationWithMember]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Real-time subscription
  useEffect(() => {
    if (!activeConversation?.id) return;
    if (channelRef?.current) supabase?.removeChannel(channelRef?.current);

    channelRef.current = supabase?.channel(`messages_${activeConversation?.id}`)?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'direct_messages',
        filter: `conversation_id=eq.${activeConversation?.id}`
      }, async (payload) => {
        const { data: msgWithSender } = await supabase?.from('direct_messages')?.select('*, sender:user_profiles!direct_messages_sender_id_fkey(id, full_name, avatar_url)')?.eq('id', payload?.new?.id)?.single();
        if (msgWithSender) {
          setMessages(prev => [...prev, msgWithSender]);
          if (msgWithSender?.sender_id !== user?.id) {
            await supabase?.from('direct_messages')?.update({ is_read: true })?.eq('id', msgWithSender?.id);
          }
        }
      })?.subscribe();

    return () => {
      if (channelRef?.current) supabase?.removeChannel(channelRef?.current);
    };
  }, [activeConversation?.id, user?.id]);

  const handleSend = async () => {
    if (!newMessage?.trim() || !activeConversation?.id || sending) return;
    setSending(true);
    const content = newMessage?.trim();
    setNewMessage('');
    try {
      await supabase?.from('direct_messages')?.insert({
        conversation_id: activeConversation?.id,
        sender_id: user?.id,
        content
      });
      await supabase?.from('conversations')?.update({ last_message_at: new Date()?.toISOString() })?.eq('id', activeConversation?.id);
    } catch (err) {
      console.error('Send message error:', err);
      setNewMessage(content);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
      />
      {/* Panel */}
      <div
        className="fixed right-0 top-0 h-full flex flex-col z-50 shadow-2xl"
        style={{ width: '420px', maxWidth: '100vw', background: 'var(--color-background)' }}
      >
        {/* Header */}
        <div
          className="flex items-center gap-3 px-4 py-3 border-b flex-shrink-0"
          style={{ background: 'var(--color-primary)', borderColor: 'rgba(133,113,77,0.2)' }}
        >
          {activeConversation ? (
            <>
              <button
                onClick={() => setActiveConversation(null)}
                className="text-white/70 hover:text-white transition-colors"
              >
                <Icon name="ArrowLeft" size={18} color="currentColor" />
              </button>
              <div className="w-9 h-9 rounded-full overflow-hidden border" style={{ borderColor: 'var(--color-accent)' }}>
                <Image
                  src={activeConversation?.otherMember?.avatar_url}
                  alt={activeConversation?.otherMember?.full_name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-heading font-semibold text-sm text-white truncate">
                  {activeConversation?.otherMember?.full_name}
                </p>
                <p className="font-caption text-xs" style={{ color: 'rgba(133,113,77,0.8)' }}>Active now</p>
              </div>
            </>
          ) : (
            <>
              <Icon name="MessageCircle" size={20} color="var(--color-accent)" />
              <h3 className="font-heading font-semibold text-white flex-1">Messages</h3>
            </>
          )}
          <button onClick={onClose} className="text-white/70 hover:text-white transition-colors">
            <Icon name="X" size={20} color="currentColor" />
          </button>
        </div>

        {!activeConversation ? (
          /* Conversations List */
          (<div className="flex-1 overflow-y-auto">
            {conversations?.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full gap-3 p-8">
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'rgba(0,48,87,0.08)' }}>
                  <Icon name="MessageCircle" size={28} color="var(--color-primary)" />
                </div>
                <p className="font-caption text-sm text-center" style={{ color: 'var(--color-muted-foreground)' }}>
                  No conversations yet. Message a member to get started!
                </p>
              </div>
            ) : (
              conversations?.map((conv) => (
                <button
                  key={conv?.id}
                  onClick={() => { setActiveConversation(conv); loadMessages(conv?.id); }}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors border-b text-left"
                  style={{ borderColor: 'var(--color-border)' }}
                >
                  <div className="relative flex-shrink-0">
                    <div className="w-11 h-11 rounded-full overflow-hidden border" style={{ borderColor: 'var(--color-accent)' }}>
                      <Image
                        src={conv?.otherMember?.avatar_url}
                        alt={conv?.otherMember?.full_name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {conv?.unreadCount > 0 && (
                      <span
                        className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-xs font-caption font-bold text-white"
                        style={{ background: 'var(--color-accent)', color: 'var(--color-primary)' }}
                      >
                        {conv?.unreadCount}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="font-caption font-semibold text-sm truncate" style={{ color: 'var(--color-foreground)' }}>
                        {conv?.otherMember?.full_name}
                      </p>
                      <span className="font-caption text-xs flex-shrink-0 ml-2" style={{ color: 'var(--color-muted-foreground)' }}>
                        {formatDate(conv?.lastMessage?.created_at)}
                      </span>
                    </div>
                    <p className="font-caption text-xs truncate" style={{ color: 'var(--color-muted-foreground)' }}>
                      {conv?.lastMessage?.content || 'No messages yet'}
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>)
        ) : (
          /* Chat View */
          (<>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--color-accent)', borderTopColor: 'transparent' }} />
                </div>
              ) : messages?.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full gap-2 py-12">
                  <Icon name="MessageCircle" size={32} color="var(--color-muted-foreground)" />
                  <p className="font-caption text-sm" style={{ color: 'var(--color-muted-foreground)' }}>Start the conversation!</p>
                </div>
              ) : (
                messages?.map((msg, idx) => {
                  const isSender = msg?.sender_id === user?.id;
                  const showDate = idx === 0 || formatDate(messages?.[idx - 1]?.created_at) !== formatDate(msg?.created_at);
                  return (
                    <div key={msg?.id}>
                      {showDate && (
                        <div className="flex justify-center my-2">
                          <span className="px-3 py-1 rounded-full text-xs font-caption" style={{ background: 'var(--color-muted)', color: 'var(--color-muted-foreground)' }}>
                            {formatDate(msg?.created_at)}
                          </span>
                        </div>
                      )}
                      <div className={`flex items-end gap-2 ${isSender ? 'flex-row-reverse' : 'flex-row'}`}>
                        {!isSender && (
                          <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0 border" style={{ borderColor: 'var(--color-accent)' }}>
                            <Image src={msg?.sender?.avatar_url} alt={msg?.sender?.full_name} className="w-full h-full object-cover" />
                          </div>
                        )}
                        <div className={`max-w-xs ${isSender ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                          <div
                            className="px-3 py-2 rounded-xl text-sm font-body leading-relaxed"
                            style={isSender
                              ? { background: 'var(--color-accent)', color: 'var(--color-primary)', borderBottomRightRadius: '4px' }
                              : { background: 'white', color: 'var(--color-foreground)', border: '1px solid var(--color-border)', borderBottomLeftRadius: '4px' }
                            }
                          >
                            {msg?.content}
                          </div>
                          <div className={`flex items-center gap-1 ${isSender ? 'flex-row-reverse' : 'flex-row'}`}>
                            <span className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
                              {formatTime(msg?.created_at)}
                            </span>
                            {isSender && (
                              <Icon name={msg?.is_read ? 'CheckCheck' : 'Check'} size={12} color={msg?.is_read ? 'var(--color-accent)' : 'var(--color-muted-foreground)'} />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>
            {/* Input */}
            <div className="p-3 border-t flex-shrink-0" style={{ borderColor: 'var(--color-border)' }}>
              <div className="flex items-end gap-2">
                <div
                  className="flex-1 flex items-end gap-2 rounded-xl px-3 py-2 border"
                  style={{ borderColor: 'var(--color-border)', background: 'var(--color-surface)' }}
                >
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e?.target?.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type a message..."
                    rows={1}
                    className="flex-1 bg-transparent resize-none outline-none font-body text-sm leading-relaxed"
                    style={{ color: 'var(--color-foreground)', maxHeight: '80px' }}
                  />
                  <button className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0">
                    <Icon name="Smile" size={18} color="currentColor" />
                  </button>
                  <button className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0">
                    <Icon name="Paperclip" size={18} color="currentColor" />
                  </button>
                </div>
                <button
                  onClick={handleSend}
                  disabled={!newMessage?.trim() || sending}
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-200 disabled:opacity-50"
                  style={{ background: 'var(--color-accent)' }}
                >
                  <Icon name="Send" size={16} color="var(--color-primary)" />
                </button>
              </div>
            </div>
          </>)
        )}
      </div>
    </>
  );
}
